import { useBaseCheckoutStore } from '@/composables/useBaseCheckoutStore'
import { STORAGE_KEYS } from '@/constants/CheckoutConstants'
import { clearBraintreeClient } from '@/services/Braintree'
import { Address } from '@/types/address'
import { PaymentMethodData } from '@/types/payment'
import {
  RemovableRef,
  StorageSerializers,
  useSessionStorage
} from '@vueuse/core'
import { defineStore } from 'pinia'

interface GuestCheckoutState {
  isOrderPending: boolean
  email: RemovableRef<string | null>
  initialAddressData: RemovableRef<Address | null>
  selectedShippingAddressData: RemovableRef<Address | null>
  selectedBillingAddressData: RemovableRef<Address | null>
  selectedPaymentMethodData: RemovableRef<PaymentMethodData | null>
  useSameBillingAddress: RemovableRef<boolean | null>
  subscribeToNewsletter: RemovableRef<boolean | null>
  doiOptIn: RemovableRef<boolean | null>
  phoneNumber: RemovableRef<string | null>
  ageData: RemovableRef<object | null>
  selectedDeliveryOptionsMap: RemovableRef<object | null>
  blacklistedPaymentMethodCodes: Array<string>
}

export const useGuestCheckoutStore = defineStore('guestCheckout', {
  state: (): GuestCheckoutState => ({
    isOrderPending: false,
    email: useSessionStorage(STORAGE_KEYS.EMAIL, null),
    initialAddressData: useSessionStorage(
      STORAGE_KEYS.INITIAL_ADDRESS_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    selectedShippingAddressData: useSessionStorage(
      STORAGE_KEYS.SELECTED_SHIPPING_ADDRESS_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    selectedBillingAddressData: useSessionStorage(
      STORAGE_KEYS.SELECTED_BILLING_ADDRESS_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    selectedPaymentMethodData: useSessionStorage(
      STORAGE_KEYS.SELECTED_PAYMENT_METHOD_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    useSameBillingAddress: useSessionStorage(
      STORAGE_KEYS.USE_SAME_BILLING_ADDRESS,
      null
    ),
    subscribeToNewsletter: useSessionStorage(
      STORAGE_KEYS.SUBSCRIBE_TO_NEWSLETTER,
      null
    ),
    doiOptIn: useSessionStorage(STORAGE_KEYS.DOI_OPT_IN, null),
    phoneNumber: useSessionStorage(STORAGE_KEYS.PHONE_NUMBER, null),
    ageData: useSessionStorage(STORAGE_KEYS.AGE_DATA, null, {
      serializer: StorageSerializers.object
    }),
    selectedDeliveryOptionsMap: useSessionStorage(
      STORAGE_KEYS.SELECTED_DELIVERY_OPTIONS_MAP,
      null,
      { serializer: StorageSerializers.object }
    ),
    blacklistedPaymentMethodCodes: []
  }),

  getters: {
    hasEmail(): boolean {
      const { hasEmail } = useBaseCheckoutStore(this)
      return hasEmail.value
    },
    hasShippingAddress(): boolean {
      const { hasShippingAddress } = useBaseCheckoutStore(this)
      return hasShippingAddress.value
    },
    hasBillingAddress(): boolean {
      const { hasBillingAddress } = useBaseCheckoutStore(this)
      return hasBillingAddress.value
    },
    hasPaymentMethod(): boolean {
      const { hasPaymentMethod } = useBaseCheckoutStore(this)
      return hasPaymentMethod.value
    }
  },

  actions: {
    clearData() {
      this.isOrderPending = false
      this.useSameBillingAddress = null
      this.selectedPaymentMethodData = null
      this.selectedDeliveryOptionsMap = null
      this.initialAddressData = null
      this.selectedShippingAddressData = null
      this.selectedBillingAddressData = null
      this.email = null
      this.subscribeToNewsletter = null
      this.doiOptIn = null
      this.phoneNumber = null
      this.ageData = null
      this.blacklistedPaymentMethodCodes = []
      clearBraintreeClient()
    }
  }
})
